import ExpandDownCircleFilled from '@ixigo/icons/ExpandDownCircleFilled';
import { InlineAlert } from '@ixigo/iui/InlineAlert';
import Typography from '@ixigo/iui/Typography';
import { RupeeChecked } from '@repo/shared/icons';
import { useEffect, useState } from 'react';
import { type CabUpdateResponse, getBookingDetail, getCabUpdates } from '@/api';
import {
	CabCommunication,
	CabDriverDetails,
	OTPSection,
	useCabUpdatesPolling,
	useTripActivation,
} from '@/components/cab-update';
import { useTripBookingActions } from '../../../-store';
import { CustomerHelp } from '../../customer-help';
import SOSFab from '../../sos';
import type { TripWidgetProps } from '../widget-container';
import { ActionButtons } from './action-buttons';
import { PricingUpdate } from './pricing-update';

interface ConfirmWidgetProps extends TripWidgetProps {}

export const ConfirmWidget = ({
	bookingDetail,
	refundDetail,
	cancellationDetail,
	rescheduleDetail,
}: ConfirmWidgetProps) => {
	const {
		eticketUrl,
		invoiceUrl,
		bookingId,
		bookingStatus,
		advanceReceiptUrl,
	} = bookingDetail;
	const refundMsg = refundDetail?.msg;
	const cancelRideMsg = cancellationDetail?.msg;
	const { setBookingData } = useTripBookingActions();

	const [cabUpdate, setCabUpdate] = useState<CabUpdateResponse | null>(null);

	useEffect(() => {
		const getCabUpdate = async () => {
			const response = await getCabUpdates(bookingId);
			if (!('errors' in response)) {
				setCabUpdate(response.data);
			}
		};
		getCabUpdate();
	}, [bookingId]);

	const { isTripActive } = useTripActivation({
		tripActivationTime: cabUpdate?.tripActivationTime?.time ?? '',
		pollingIntervalMs: cabUpdate?.pollingIntervalMs ?? 0,
	});

	const { cabUpdate: pollingCabUpdate } = useCabUpdatesPolling({
		bookingId,
		isTripActive,
		pollingIntervalMs: cabUpdate?.pollingIntervalMs ?? 0,
	});

	const handleStopPolling = () => {
		getBookingDetail(bookingId).then((res) => {
			const hasError = 'errors' in res;
			if (!hasError) {
				setBookingData(res.data);
			}
		});
	};

	const syncedCabUpdate = pollingCabUpdate || cabUpdate;

	return (
		<div className="flex flex-col">
			<CabCommunication
				bookingDetail={bookingDetail}
				communication={syncedCabUpdate}
				isTripActive={isTripActive}
				onStopPolling={handleStopPolling}
			/>
			<div className="flex flex-col gap-15 p-15">
				{refundMsg && (
					<InlineAlert
						severity="success"
						textColor="auto"
						className="items-start"
						title={
							<Typography
								variant="body-xs"
								// biome-ignore lint/security/noDangerouslySetInnerHtml: <static data just to show refund msg>
								dangerouslySetInnerHTML={{
									__html: refundMsg,
								}}
							/>
						}
						icon={<RupeeChecked className="flex-shrink-0" />}
						closeBtnContent={<ExpandDownCircleFilled />}
						onClose={() => {
							document
								.getElementById('refund-details')
								?.scrollIntoView({ behavior: 'smooth', block: 'start' });
						}}
					/>
				)}
				{cancelRideMsg && (
					<InlineAlert
						severity="warning"
						textColor="auto"
						className="items-center"
						title={
							<Typography
								variant="body-xs"
								// biome-ignore lint/security/noDangerouslySetInnerHtml: <static data just to show refund msg>
								dangerouslySetInnerHTML={{
									__html: cancelRideMsg,
								}}
							/>
						}
						closeBtnContent={<ExpandDownCircleFilled />}
						onClose={() => {
							document
								.getElementById('refund-status')
								?.scrollIntoView({ behavior: 'smooth', block: 'start' });
						}}
					/>
				)}
				{(syncedCabUpdate?.cab ||
					syncedCabUpdate?.driver ||
					syncedCabUpdate?.pin) && (
					<div className="flex flex-col items-start justify-start gap-5">
						{(syncedCabUpdate?.cab || syncedCabUpdate?.driver) && (
							<CabDriverDetails {...syncedCabUpdate} />
						)}
						{syncedCabUpdate.pin && (
							<OTPSection
								otp={{
									label: syncedCabUpdate.pin.label,
									value: syncedCabUpdate.pin.value,
								}}
							/>
						)}
					</div>
				)}
				<ActionButtons
					actions={{
						eticketUrl,
						invoiceUrl,
						advanceReceiptUrl,
						showModifyBooking:
							cancellationDetail?.allowed || rescheduleDetail?.allowed,
					}}
					className={
						cabUpdate?.cab || cabUpdate?.driver || cabUpdate?.pin
							? 'border-t border-tertiary pt-15'
							: ''
					}
				/>
				{bookingStatus === 'CANCELLED' && (
					<CustomerHelp className="border border-tertiary rounded-20 overflow-hidden p-15 pb-10" />
				)}
			</div>
			{syncedCabUpdate?.pricingInfo && (
				<PricingUpdate
					pricingInfo={syncedCabUpdate.pricingInfo}
					bookingId={bookingId}
				/>
			)}
			{syncedCabUpdate?.emergencyContact && (
				<SOSFab emergencyContact={syncedCabUpdate.emergencyContact} />
			)}
		</div>
	);
};
